<template>
    <UserLayout :page-title="t('settings')" :page-section="t('account')">
        <div class="st-wrap">

            <!-- Welcome Strip -->
            <div class="st-welcome anim-up" style="--d:0ms">
                <div class="st-welcome-left">
                    <div class="st-welcome-icon">
                        <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <div>
                        <div class="st-welcome-eyebrow">{{ t('account') }}</div>
                        <div class="st-welcome-title">{{ t('settings') }}</div>
                    </div>
                </div>
                <div class="st-welcome-chips">
                    <span class="st-chip st-chip--purple">
                        <span class="st-chip-dot"></span>{{ t('profileInformation') }}
                    </span>
                    <span class="st-chip st-chip--green">
                        <span class="st-chip-dot" style="background:#22c55e"></span>{{ t('kycVerification') }}
                    </span>
                </div>
            </div>

            <!-- 2-column grid -->
            <div class="st-grid">

                <!-- LEFT COLUMN -->
                <div class="st-col">

                    <!-- Profile Form -->
                    <div class="st-card anim-up" style="--d:60ms">
                        <div class="st-card-head">
                            <span class="st-card-title">{{ t('profileInformation') }}</span>
                        </div>

                        <form @submit.prevent="updateProfile" class="st-form">
                            <!-- Avatar row -->
                            <div class="st-avatar-row">
                                <div class="st-avatar-wrap">
                                    <div class="st-avatar-ring">
                                        <img v-if="avatarPreview || currentUser.avatar_url"
                                            :src="avatarPreview || currentUser.avatar_url"
                                            alt="Profile"
                                            class="st-avatar-img" />
                                        <div v-else class="st-avatar-initials">
                                            {{ (currentUser.display_name || currentUser.name || 'WalletConnect User').charAt(0).toUpperCase() }}
                                        </div>
                                    </div>
                                    <input ref="avatarInput" type="file" accept="image/jpeg,image/png,image/jpg"
                                        @change="handleAvatarChange" class="st-hidden-input" />
                                    <button type="button" @click="$refs.avatarInput.click()"
                                        :disabled="avatarUploading" class="st-avatar-btn">
                                        <svg v-if="!avatarUploading" width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <svg v-else class="st-spin" width="14" height="14" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </button>
                                </div>
                                <div class="st-avatar-meta">
                                    <p class="st-field-label">{{ t('profilePhoto') }}</p>
                                    <p class="st-avatar-hint">{{ t('jpgPngMax2mb') }}</p>
                                    <button v-if="currentUser.avatar_url" type="button" @click="removeAvatar"
                                        :disabled="avatarRemoving" class="st-remove-btn">
                                        {{ avatarRemoving ? t('removing') + '...' : t('removePhoto') }}
                                    </button>
                                    <div v-if="user.uid" class="st-uid-badge" @click="copyToClipboard(user.uid)" title="Click to copy UID">
                                        <span class="st-uid-badge-label">UID</span>
                                        <span class="st-uid-badge-val">{{ user.uid }}</span>
                                        <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="opacity:0.5;flex-shrink:0">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <!-- Name -->
                            <div class="st-field">
                                <label class="st-field-label">{{ t('fullName') }}</label>
                                <input
                                    v-model="profileForm.name"
                                    type="text"
                                    class="st-input"
                                    :placeholder="connectedWalletAddress || t('enterFullName') || 'Enter your full name'"
                                />
                                <p v-if="errors.name" class="st-err">{{ errors.name }}</p>
                            </div>

                            <!-- Email -->
                            <div class="st-field">
                                <label class="st-field-label">{{ t('email') }}</label>
                                <input v-model="profileForm.email" type="email" class="st-input" />
                                <p v-if="errors.email" class="st-err">{{ errors.email }}</p>
                            </div>

                            <!-- Gender -->
                            <div class="st-field">
                                <label class="st-field-label">{{ t('gender') }}</label>
                                <select v-model="profileForm.gender" class="st-input st-select">
                                    <option value="">{{ t('selectGender') }}</option>
                                    <option value="male">{{ t('male') }}</option>
                                    <option value="female">{{ t('female') }}</option>
                                    <option value="other">{{ t('other') }}</option>
                                </select>
                            </div>

                            <button type="submit" :disabled="isProcessing" class="st-submit-btn">
                                <span v-if="isProcessing">{{ t('updating') }}...</span>
                                <span v-else>{{ t('updateProfile') }}</span>
                            </button>
                        </form>
                    </div>

                    <!-- Password Form -->
                    <div class="st-card anim-up" style="--d:120ms">
                        <div class="st-card-head">
                            <span class="st-card-title">{{ t('updatePassword') }}</span>
                        </div>

                        <form @submit.prevent="updatePassword" class="st-form">
                            <!-- Current Password -->
                            <div class="st-field">
                                <label class="st-field-label">{{ t('currentPassword') }}</label>
                                <div class="st-pass-wrap">
                                    <input v-model="passwordForm.current_password"
                                        :type="showCurrentPassword ? 'text' : 'password'"
                                        :placeholder="t('enterCurrentPassword')"
                                        class="st-input st-input--pass" />
                                    <button type="button" @click="showCurrentPassword = !showCurrentPassword" class="st-eye-btn">
                                        <svg v-if="showCurrentPassword" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        <svg v-else width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    </button>
                                </div>
                                <p v-if="passwordErrors.current_password" class="st-err">{{ passwordErrors.current_password }}</p>
                            </div>

                            <!-- New Password -->
                            <div class="st-field">
                                <label class="st-field-label">{{ t('newPassword') }}</label>
                                <div class="st-pass-wrap">
                                    <input v-model="passwordForm.password"
                                        :type="showNewPassword ? 'text' : 'password'"
                                        :placeholder="t('enterNewPassword')"
                                        class="st-input st-input--pass" />
                                    <button type="button" @click="showNewPassword = !showNewPassword" class="st-eye-btn">
                                        <svg v-if="showNewPassword" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        <svg v-else width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    </button>
                                </div>
                                <p v-if="passwordErrors.password" class="st-err">{{ passwordErrors.password }}</p>
                                <p class="st-hint">{{ t('passwordRequirements') }}</p>
                            </div>

                            <!-- Confirm Password -->
                            <div class="st-field">
                                <label class="st-field-label">{{ t('confirmNewPassword') }}</label>
                                <div class="st-pass-wrap">
                                    <input v-model="passwordForm.password_confirmation"
                                        :type="showConfirmPassword ? 'text' : 'password'"
                                        :placeholder="t('confirmYourNewPassword')"
                                        class="st-input st-input--pass" />
                                    <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="st-eye-btn">
                                        <svg v-if="showConfirmPassword" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        <svg v-else width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    </button>
                                </div>
                                <p v-if="passwordErrors.password_confirmation" class="st-err">{{ passwordErrors.password_confirmation }}</p>
                            </div>

                            <button type="submit" :disabled="passwordProcessing" class="st-submit-btn">
                                <span v-if="passwordProcessing">{{ t('updating') }}...</span>
                                <span v-else>{{ t('updatePassword') }}</span>
                            </button>
                        </form>
                    </div>
                </div>

                <!-- RIGHT COLUMN -->
                <div class="st-col">

                    <!-- Account Information -->
                    <div class="st-card anim-up" style="--d:80ms">
                        <div class="st-card-head">
                            <span class="st-card-title">{{ t('accountInformation') }}</span>
                        </div>

                        <div class="st-info-list">
                            <!-- UID -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('uid') || 'User ID (UID)' }}</p>
                                    <p class="st-info-val st-mono st-uid-val">{{ user.uid || t('notAvailable') }}</p>
                                </div>
                                <button v-if="user.uid" @click="copyToClipboard(user.uid)" class="st-copy-btn">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>

                            <!-- Login Email -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('loginEmail') || 'Login Email' }}</p>
                                    <p class="st-info-val st-mono">{{ user.email || t('notAvailable') }}</p>
                                </div>
                                <button v-if="user.email" @click="copyToClipboard(user.email)" class="st-copy-btn">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>

                            <!-- Wallet Address -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('walletAddress') }}</p>
                                    <p class="st-info-val st-mono">
                                        {{ connectedWalletAddress
                                            ? connectedWalletAddress.substring(0, 10) + '...' + connectedWalletAddress.substring(connectedWalletAddress.length - 8)
                                            : t('notAvailable') }}
                                    </p>
                                </div>
                                <button v-if="connectedWalletAddress" @click="copyWalletAddress" class="st-copy-btn">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>

                            <!-- Wallet Balance -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('walletBalance') }}</p>
                                    <p class="st-info-val">{{ formatCurrency(user.wallet?.balance || 0) }}</p>
                                </div>
                            </div>

                            <!-- Bonus Balance -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('bonusBalance') }}</p>
                                    <p class="st-info-val">{{ formatCurrency(user.wallet?.bonus_balance || 0) }}</p>
                                </div>
                            </div>

                            <!-- Total Invested -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('totalInvested') }}</p>
                                    <p class="st-info-val">{{ formatCurrency(user.wallet?.total_invested || 0) }}</p>
                                </div>
                            </div>

                            <!-- Total Withdrawn -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('totalWithdraw') }}</p>
                                    <p class="st-info-val">{{ formatCurrency(user.wallet?.total_withdrawn || 0) }}</p>
                                </div>
                            </div>

                            <!-- Referral Code -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('referralCode') }}</p>
                                    <p class="st-info-val st-mono">{{ user.referral_code }}</p>
                                </div>
                                <button @click="copyReferralCode" class="st-copy-btn">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>

                            <!-- Member Since -->
                            <div class="st-info-row">
                                <div class="st-info-content">
                                    <p class="st-info-label">{{ t('memberSince') }}</p>
                                    <p class="st-info-val">{{ formatDate(user.created_at) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Wallet Address Book link -->
                    <div class="st-card anim-up" style="--d:120ms">
                        <div class="st-card-head">
                            <span class="st-card-title">Wallet Address Book</span>
                        </div>
                        <p style="font-size:12px;color:rgba(255,255,255,0.45);margin-bottom:14px;">
                            Manage all your connected BSC wallets in one place. Set a primary wallet, edit labels, and add new wallets via WalletConnect.
                        </p>
                        <a href="/user/settings/wallets" class="st-submit-btn" style="display:flex;align-items:center;justify-content:center;gap:7px;text-decoration:none;">
                            <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                            </svg>
                            Manage Wallet Addresses
                        </a>
                    </div>

                    <!-- KYC Verification -->
                    <div class="st-card anim-up" style="--d:160ms">
                        <div class="st-card-head">
                            <span class="st-card-title">{{ t('kycVerification') }}</span>
                            <span class="st-kyc-badge" :class="'st-kyc-badge--' + (user.kyc_status || 'pending')">
                                {{ getKycStatusLabel(user.kyc_status) }}
                            </span>
                        </div>

                        <!-- KYC Form (pending / rejected / no status) -->
                        <div v-if="!user.kyc_status || user.kyc_status === 'pending' || user.kyc_status === 'rejected'">
                            <form @submit.prevent="submitKyc" class="st-form">
                                <!-- First / Last Name -->
                                <div class="st-field-grid">
                                    <div class="st-field">
                                        <label class="st-field-label">{{ t('firstName') }}</label>
                                        <input v-model="kycForm.first_name" type="text" class="st-input" />
                                        <p v-if="errors.first_name" class="st-err">{{ errors.first_name }}</p>
                                    </div>
                                    <div class="st-field">
                                        <label class="st-field-label">{{ t('lastName') }}</label>
                                        <input v-model="kycForm.last_name" type="text" class="st-input" />
                                        <p v-if="errors.last_name" class="st-err">{{ errors.last_name }}</p>
                                    </div>
                                </div>

                                <!-- DOB -->
                                <div class="st-field">
                                    <label class="st-field-label">{{ t('dateOfBirth') }}</label>
                                    <input v-model="kycForm.date_of_birth" type="date" class="st-input" />
                                    <p v-if="errors.date_of_birth" class="st-err">{{ errors.date_of_birth }}</p>
                                </div>

                                <!-- Phone -->
                                <div class="st-field">
                                    <label class="st-field-label">{{ t('phoneNumber') }}</label>
                                    <input v-model="kycForm.phone" type="tel" class="st-input" />
                                    <p v-if="errors.phone" class="st-err">{{ errors.phone }}</p>
                                </div>

                                <!-- Address -->
                                <div class="st-field">
                                    <label class="st-field-label">{{ t('address') }}</label>
                                    <input v-model="kycForm.address" type="text" class="st-input" />
                                    <p v-if="errors.address" class="st-err">{{ errors.address }}</p>
                                </div>

                                <!-- City / State -->
                                <div class="st-field-grid">
                                    <div class="st-field">
                                        <label class="st-field-label">{{ t('city') }}</label>
                                        <input v-model="kycForm.city" type="text" class="st-input" />
                                        <p v-if="errors.city" class="st-err">{{ errors.city }}</p>
                                    </div>
                                    <div class="st-field">
                                        <label class="st-field-label">{{ t('state') }}</label>
                                        <input v-model="kycForm.state" type="text" class="st-input" />
                                        <p v-if="errors.state" class="st-err">{{ errors.state }}</p>
                                    </div>
                                </div>

                                <!-- Country / Postal -->
                                <div class="st-field-grid">
                                    <div class="st-field">
                                        <label class="st-field-label">{{ t('country') }}</label>
                                        <input v-model="kycForm.country" type="text" class="st-input" />
                                        <p v-if="errors.country" class="st-err">{{ errors.country }}</p>
                                    </div>
                                    <div class="st-field">
                                        <label class="st-field-label">{{ t('postalCode') }}</label>
                                        <input v-model="kycForm.postal_code" type="text" class="st-input" />
                                        <p v-if="errors.postal_code" class="st-err">{{ errors.postal_code }}</p>
                                    </div>
                                </div>

                                <!-- Doc Type / Number -->
                                <div class="st-field-grid">
                                    <div class="st-field">
                                        <label class="st-field-label">{{ t('documentType') }}</label>
                                        <select v-model="kycForm.document_type" class="st-input st-select">
                                            <option value="">{{ t('selectDocumentType') }}</option>
                                            <option value="passport">{{ t('passport') }}</option>
                                            <option value="driving_license">{{ t('drivingLicense') }}</option>
                                            <option value="national_id">{{ t('nationalId') }}</option>
                                        </select>
                                        <p v-if="errors.document_type" class="st-err">{{ errors.document_type }}</p>
                                    </div>
                                    <div class="st-field">
                                        <label class="st-field-label">{{ t('documentNumber') }}</label>
                                        <input v-model="kycForm.document_number" type="text" class="st-input" />
                                        <p v-if="errors.document_number" class="st-err">{{ errors.document_number }}</p>
                                    </div>
                                </div>

                                <!-- Upload Documents -->
                                <div class="st-field">
                                    <p class="st-field-label" style="margin-bottom:12px">{{ t('uploadDocuments') }}</p>

                                    <!-- Front Image -->
                                    <div class="st-upload-block">
                                        <label class="st-field-label">{{ t('documentFrontImage') }}</label>
                                        <input ref="frontImageInput" type="file" accept="image/jpeg,image/png,image/jpg"
                                            @change="handleFrontImageChange" class="st-hidden-input" />
                                        <button type="button" @click="$refs.frontImageInput.click()" class="st-drop-zone">
                                            <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            <span class="st-drop-label">{{ kycForm.front_image_name || t('clickToUploadFront') }}</span>
                                        </button>
                                        <p v-if="errors.front_image" class="st-err">{{ errors.front_image }}</p>
                                    </div>

                                    <!-- Back Image -->
                                    <div class="st-upload-block">
                                        <label class="st-field-label">{{ t('documentBackImage') }}</label>
                                        <input ref="backImageInput" type="file" accept="image/jpeg,image/png,image/jpg"
                                            @change="handleBackImageChange" class="st-hidden-input" />
                                        <button type="button" @click="$refs.backImageInput.click()" class="st-drop-zone">
                                            <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            <span class="st-drop-label">{{ kycForm.back_image_name || t('clickToUploadBack') }}</span>
                                        </button>
                                        <p v-if="errors.back_image" class="st-err">{{ errors.back_image }}</p>
                                    </div>

                                    <!-- Selfie -->
                                    <div class="st-upload-block">
                                        <label class="st-field-label">{{ t('selfieWithDocument') }}</label>
                                        <input ref="selfieInput" type="file" accept="image/jpeg,image/png,image/jpg"
                                            @change="handleSelfieChange" class="st-hidden-input" />
                                        <button type="button" @click="$refs.selfieInput.click()" class="st-drop-zone">
                                            <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            <span class="st-drop-label">{{ kycForm.selfie_name || t('clickToUploadSelfie') }}</span>
                                        </button>
                                        <p v-if="errors.selfie" class="st-err">{{ errors.selfie }}</p>
                                    </div>
                                </div>

                                <button type="submit" :disabled="kycProcessing" class="st-submit-btn">
                                    <span v-if="kycProcessing">{{ t('submitting') }}...</span>
                                    <span v-else>{{ t('submitKyc') }}</span>
                                </button>
                            </form>
                        </div>

                        <!-- Under Review -->
                        <div v-else-if="user.kyc_status === 'reviewing'" class="st-kyc-state">
                            <div class="st-kyc-state-icon st-kyc-state-icon--amber">
                                <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p class="st-kyc-state-text">{{ t('kycUnderReview') }}</p>
                        </div>

                        <!-- Approved -->
                        <div v-else-if="user.kyc_status === 'approved'" class="st-kyc-state">
                            <div class="st-kyc-state-icon st-kyc-state-icon--green">
                                <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p class="st-kyc-state-text">{{ t('kycApproved') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </UserLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue"
import { useToast } from "@/composables/useToast.js"
const props = defineProps({
    user: {
        type: Object,
        required: true
    }
})

const page = usePage()
const { t } = useTranslation()
const { showToast } = useToast()

const isProcessing = ref(false)
const avatarUploading = ref(false)
const avatarRemoving = ref(false)
const kycProcessing = ref(false)
const passwordProcessing = ref(false)
const avatarPreview = ref(null)

const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

const currentUser = ref({ ...props.user })
const connectedWalletAddress = computed(() => props.user.wallet_address || props.user.tron_wallet_address || '')

const errors = computed(() => page.props.errors || {})
const passwordErrors = computed(() => {
    const err = page.props.errors || {}
    return {
        current_password: err.current_password,
        password: err.password,
        password_confirmation: err.password_confirmation
    }
})
const primaryColor = computed(() => page.props.primaryColor || '#86efac')

const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 134, g: 239, b: 172 }
}

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex)
    const newR = Math.min(255, Math.max(0, rgb.r + amount))
    const newG = Math.min(255, Math.max(0, rgb.g + amount))
    const newB = Math.min(255, Math.max(0, rgb.b + amount))
    return "#" + ((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1)
}

const themeColors = computed(() => {
    const rgb = hexToRgb(primaryColor.value)
    return {
        primary: primaryColor.value,
        primaryDark: adjustColor(primaryColor.value, -20),
        primaryRgb: `${rgb.r}, ${rgb.g}, ${rgb.b}`
    }
})

const isDefaultName = (name) => !name || name === 'WalletConnect User'

const profileForm = ref({
    name: isDefaultName(props.user.name) ? '' : props.user.name,
    email: props.user.email,
    gender: props.user.gender
})

const passwordForm = ref({
    current_password: '',
    password: '',
    password_confirmation: ''
})

const kycForm = ref({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    document_type: '',
    document_number: '',
    front_image: null,
    back_image: null,
    selfie: null,
    front_image_name: '',
    back_image_name: '',
    selfie_name: ''
})

const handleAvatarChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
        showToast(t('selectValidImageFile') || 'Please select a valid image file', 'error')
        return
    }

    if (file.size > 2 * 1024 * 1024) {
        showToast(t('imageSizeTooLarge') || 'Image size must not exceed 2MB', 'error')
        return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
        avatarPreview.value = e.target.result
    }
    reader.readAsDataURL(file)

    avatarUploading.value = true
    const formData = new FormData()
    formData.append('avatar', file)
    formData.append('name', profileForm.value.name)
    formData.append('email', profileForm.value.email)
    formData.append('gender', profileForm.value.gender || '')

    router.post('/user/settings/profile', formData, {
        preserveScroll: true,
        onSuccess: (page) => {
            avatarPreview.value = null
            if (page.props.user) {
                currentUser.value = { ...page.props.user }
            }
            showToast(t('avatarUpdatedSuccess') || 'Avatar updated successfully!', 'success')
        },
        onError: () => {
            avatarPreview.value = null
            showToast(t('avatarUpdateFailed') || 'Failed to update avatar', 'error')
        },
        onFinish: () => {
            avatarUploading.value = false
            event.target.value = ''
        }
    })
}

const removeAvatar = () => {
    avatarRemoving.value = true

    router.delete('/user/settings/avatar', {
        preserveScroll: true,
        onSuccess: (page) => {
            currentUser.value.avatar = null
            currentUser.value.avatar_url = null
            if (page.props.user) {
                currentUser.value = { ...page.props.user }
            }
            showToast(t('avatarRemovedSuccess') || 'Avatar removed successfully!', 'success')
        },
        onError: () => {
            showToast(t('avatarRemoveFailed') || 'Failed to remove avatar', 'error')
        },
        onFinish: () => {
            avatarRemoving.value = false
        }
    })
}

const copyToClipboard = async (text) => {
    if (!text) return
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text)
        } else {
            const el = document.createElement('textarea')
            el.value = text
            el.style.position = 'fixed'
            el.style.left = '-999999px'
            document.body.appendChild(el)
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
        }
        showToast(t('copied') || 'Copied!', 'success')
    } catch (err) {
        console.error('Failed to copy:', err)
    }
}

const copyWalletAddress = async () => {
    if (!connectedWalletAddress.value) return

    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(connectedWalletAddress.value)
            showToast(t('walletAddressCopied') || 'Wallet address copied!', 'success')
        } else {
            fallbackCopyToClipboard(connectedWalletAddress.value, t('walletAddressCopied') || 'Wallet address copied!')
        }
    } catch (err) {
        console.error('Failed to copy:', err)
        fallbackCopyToClipboard(connectedWalletAddress.value, t('walletAddressCopied') || 'Wallet address copied!')
    }
}

const copyReferralCode = async () => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(props.user.referral_code)
            showToast(t('referralCodeCopied') || 'Referral code copied!', 'success')
        } else {
            fallbackCopyToClipboard(props.user.referral_code, t('referralCodeCopied') || 'Referral code copied!')
        }
    } catch (err) {
        console.error('Failed to copy:', err)
        fallbackCopyToClipboard(props.user.referral_code, t('referralCodeCopied') || 'Referral code copied!')
    }
}

const fallbackCopyToClipboard = (text, successMessage) => {
    try {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        const successful = document.execCommand('copy')
        textArea.remove()

        if (successful) {
            showToast(successMessage, 'success')
        } else {
            throw new Error('Copy command was unsuccessful')
        }
    } catch (err) {
        console.error('Fallback copy failed:', err)
        showToast(t('failedToCopy') || 'Failed to copy. Please copy manually.', 'error')
    }
}

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
    }).format(amount)
}

const formatDate = (date) => {
    return new Date(date).toLocaleDateString()
}

const handleFrontImageChange = (event) => {
    const file = event.target.files[0]
    if (file) {
        kycForm.value.front_image = file
        kycForm.value.front_image_name = file.name
    }
}

const handleBackImageChange = (event) => {
    const file = event.target.files[0]
    if (file) {
        kycForm.value.back_image = file
        kycForm.value.back_image_name = file.name
    }
}

const handleSelfieChange = (event) => {
    const file = event.target.files[0]
    if (file) {
        kycForm.value.selfie = file
        kycForm.value.selfie_name = file.name
    }
}

const updateProfile = () => {
    isProcessing.value = true

    const formData = new FormData()
    formData.append('name', profileForm.value.name)
    formData.append('email', profileForm.value.email)
    formData.append('gender', profileForm.value.gender || '')

    router.post('/user/settings/profile', formData, {
        preserveScroll: true,
        onSuccess: (page) => {
            if (page.props.user) {
                currentUser.value = { ...page.props.user }
                profileForm.value.name = isDefaultName(page.props.user.name) ? '' : page.props.user.name
                profileForm.value.email = page.props.user.email
                profileForm.value.gender = page.props.user.gender
            }
            showToast(t('profileUpdatedSuccess') || 'Profile updated successfully!', 'success')
        },
        onError: () => {
            showToast(t('profileUpdateFailed') || 'Failed to update profile', 'error')
        },
        onFinish: () => {
            isProcessing.value = false
        }
    })
}

const updatePassword = () => {
    // Validation
    if (!passwordForm.value.current_password) {
        showToast(t('currentPasswordRequired') || 'Current password is required', 'error')
        return
    }

    if (!passwordForm.value.password) {
        showToast(t('newPasswordRequired') || 'New password is required', 'error')
        return
    }

    if (passwordForm.value.password.length < 8) {
        showToast(t('passwordMinLength') || 'Password must be at least 8 characters', 'error')
        return
    }

    if (passwordForm.value.password !== passwordForm.value.password_confirmation) {
        showToast(t('passwordMismatch') || 'Passwords do not match', 'error')
        return
    }

    passwordProcessing.value = true

    router.post('/user/settings/password', passwordForm.value, {
        preserveScroll: true,
        onSuccess: () => {
            // Reset form
            passwordForm.value = {
                current_password: '',
                password: '',
                password_confirmation: ''
            }
            // Reset password visibility
            showCurrentPassword.value = false
            showNewPassword.value = false
            showConfirmPassword.value = false

            showToast(t('passwordUpdatedSuccess') || 'Password updated successfully!', 'success')
        },
        onError: (errors) => {
            console.error('Password update errors:', errors)
            if (errors.current_password) {
                showToast(errors.current_password, 'error')
            } else if (errors.password) {
                showToast(errors.password, 'error')
            } else {
                showToast(t('passwordUpdateFailed') || 'Failed to update password', 'error')
            }
        },
        onFinish: () => {
            passwordProcessing.value = false
        }
    })
}

const submitKyc = () => {
    kycProcessing.value = true

    const formData = new FormData()
    formData.append('first_name', kycForm.value.first_name)
    formData.append('last_name', kycForm.value.last_name)
    formData.append('date_of_birth', kycForm.value.date_of_birth)
    formData.append('phone', kycForm.value.phone)
    formData.append('address', kycForm.value.address)
    formData.append('city', kycForm.value.city)
    formData.append('state', kycForm.value.state)
    formData.append('country', kycForm.value.country)
    formData.append('postal_code', kycForm.value.postal_code)
    formData.append('document_type', kycForm.value.document_type)
    formData.append('document_number', kycForm.value.document_number)
    formData.append('front_image', kycForm.value.front_image)
    formData.append('back_image', kycForm.value.back_image)
    formData.append('selfie', kycForm.value.selfie)

    router.post('/user/settings/kyc', formData, {
        preserveScroll: true,
        onSuccess: () => {
            kycForm.value = {
                first_name: '',
                last_name: '',
                date_of_birth: '',
                phone: '',
                address: '',
                city: '',
                state: '',
                country: '',
                postal_code: '',
                document_type: '',
                document_number: '',
                front_image: null,
                back_image: null,
                selfie: null,
                front_image_name: '',
                back_image_name: '',
                selfie_name: ''
            }
            const fileInputs = document.querySelectorAll('input[type="file"]')
            fileInputs.forEach(input => input.value = '')

            showToast(t('kycSubmittedSuccess') || 'KYC submitted successfully!', 'success')
        },
        onError: () => {
            showToast(t('kycSubmitFailed') || 'Failed to submit KYC', 'error')
        },
        onFinish: () => {
            kycProcessing.value = false
        }
    })
}

const getKycStatusStyle = (status) => {
    const styles = {
        pending: { backgroundColor: '#64748b20', color: '#64748b' },
        reviewing: { backgroundColor: '#f59e0b20', color: '#f59e0b' },
        approved: { backgroundColor: '#10b98120', color: '#10b981' },
        rejected: { backgroundColor: '#ef444420', color: '#ef4444' }
    }
    return styles[status] || styles.pending
}

const getKycStatusLabel = (status) => {
    const labels = {
        pending: t('pending'),
        reviewing: t('reviewing'),
        approved: t('approved'),
        rejected: t('rejected')
    }
    return labels[status] || status
}

onMounted(() => {
    const flash = page.props.flash
    if (flash?.success) showToast(flash.success, 'success')
    if (flash?.error) showToast(flash.error, 'error')
    if (flash?.warning) showToast(flash.warning, 'warning')
    if (flash?.info) showToast(flash.info, 'info')
})
</script>

<style scoped>
/* ── Shared animation ── */
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

/* ── Page wrapper ── */
.st-wrap {
    max-width: 1280px;
    margin: 0 auto;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
    z-index: 10;
    width: 100%;
    box-sizing: border-box;
}
@media(min-width:640px){ .st-wrap{ padding: 18px 18px; } }
@media(min-width:1024px){ .st-wrap{ padding: 20px 24px; } }

/* ── Welcome strip ── */
.st-welcome {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    background: rgba(167,93,237,0.06);
    border: 1px solid rgba(167,93,237,0.16);
    border-radius: 13px;
    padding: 12px 16px;
}
.st-welcome-left { display: flex; align-items: center; gap: 10px; }
.st-welcome-icon {
    width: 40px; height: 40px; border-radius: 11px;
    background: linear-gradient(135deg,rgba(124,58,237,0.3),rgba(167,93,237,0.2));
    border: 1px solid rgba(167,93,237,0.28);
    display: flex; align-items: center; justify-content: center;
    color: #a75ded; flex-shrink: 0;
    box-shadow: 0 0 14px rgba(167,93,237,0.22);
}
.st-welcome-eyebrow { font-size: 9px; font-weight: 700; color: rgba(167,93,237,0.7); text-transform: uppercase; letter-spacing: 0.1em; }
.st-welcome-title { font-size: 15px; font-weight: 900; color: #fff; letter-spacing: -0.02em; }
.st-welcome-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.st-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 10px; border-radius: 9px;
    font-size: 10px; font-weight: 700;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);
    color: rgba(255,255,255,0.6);
}
.st-chip-dot { width: 5px; height: 5px; border-radius: 50%; background: #a75ded; }

/* ── 2-col layout ── */
.st-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
}
@media(min-width:1024px){ .st-grid{ grid-template-columns: 1fr 1fr; align-items: start; } }

.st-col { display: flex; flex-direction: column; gap: 16px; }

/* ── Card ── */
.st-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.13);
    border-radius: 16px;
    padding: 16px;
    position: relative;
    overflow: hidden;
    transition: border-color 0.25s;
}
.st-card::before {
    content: '';
    position: absolute;
    top: 0; left: 14%; right: 14%;
    height: 1px;
    background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.st-card:hover { border-color: rgba(167,93,237,0.24); }
.st-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    flex-wrap: wrap;
    gap: 8px;
}
.st-card-title { font-size: 13px; font-weight: 800; color: #fff; letter-spacing: -0.01em; }

/* ── Form base ── */
.st-form { display: flex; flex-direction: column; gap: 14px; }
.st-field { display: flex; flex-direction: column; gap: 0; }
.st-field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media(max-width:500px){ .st-field-grid{ grid-template-columns: 1fr; } }
.st-field-label { font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; display: block; }
.st-input {
    width: 100%; box-sizing: border-box;
    padding: 9px 12px;
    background: rgba(10,4,22,0.7);
    border: 1px solid rgba(167,93,237,0.2);
    border-radius: 9px;
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
}
.st-input:focus {
    border-color: rgba(167,93,237,0.55);
    box-shadow: 0 0 0 3px rgba(167,93,237,0.1);
}
.st-select { cursor: pointer; }
.st-select option { background: #1a0a40; color: #fff; }
.st-input--pass { padding-right: 44px; }
.st-err { font-size: 11px; color: #f87171; margin-top: 4px; }
.st-hint { font-size: 10px; color: rgba(255,255,255,0.35); margin-top: 4px; }

/* ── Password wrapper ── */
.st-pass-wrap { position: relative; }
.st-eye-btn {
    position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
    background: none; border: none; cursor: pointer;
    color: rgba(255,255,255,0.4); transition: color 0.2s; padding: 0;
}
.st-eye-btn:hover { color: #a75ded; }

/* ── Submit button ── */
.st-submit-btn {
    width: 100%;
    padding: 10px 16px;
    border-radius: 9px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(135deg, #a75ded, #7c3aed);
    transition: opacity 0.2s, transform 0.15s;
}
.st-submit-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.st-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

/* ── Avatar ── */
.st-avatar-row { display: flex; align-items: center; gap: 14px; }
.st-avatar-wrap { position: relative; flex-shrink: 0; }
.st-avatar-ring {
    width: 68px; height: 68px; border-radius: 50%;
    overflow: hidden;
    background: rgba(167,93,237,0.1);
    border: 2px solid rgba(167,93,237,0.3);
}
.st-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.st-avatar-initials {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    font-size: 26px; font-weight: 700; color: #fff;
    background: linear-gradient(135deg, #a75ded, #7c3aed);
}
.st-avatar-btn {
    position: absolute; bottom: 0; right: 0;
    width: 24px; height: 24px; border-radius: 50%;
    background: #a75ded; border: 2px solid rgba(10,4,22,0.9);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #fff; transition: transform 0.15s;
}
.st-avatar-btn:hover:not(:disabled) { transform: scale(1.12); }
.st-avatar-btn:disabled { opacity: 0.5; }
.st-avatar-meta { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.st-avatar-hint { font-size: 10px; color: rgba(255,255,255,0.35); }
.st-remove-btn { font-size: 11px; font-weight: 600; color: #f87171; background: none; border: none; cursor: pointer; padding: 0; text-align: left; }
.st-remove-btn:hover { color: #fca5a5; }
.st-remove-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.st-hidden-input { display: none; }
.st-spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to{ transform: rotate(360deg); } }

/* ── Account Info List ── */
.st-info-list { display: flex; flex-direction: column; gap: 4px; }
.st-info-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 12px;
    background: rgba(10,4,22,0.5);
    border: 1px solid rgba(167,93,237,0.1);
    border-radius: 9px;
    gap: 8px;
}
.st-info-content { flex: 1; min-width: 0; }
.st-info-label { font-size: 10px; font-weight: 700; color: rgba(167,93,237,0.7); text-transform: uppercase; letter-spacing: 0.06em; }
.st-info-val { font-size: 13px; font-weight: 600; color: #fff; margin-top: 2px; word-break: break-all; }
.st-mono { font-family: 'Courier New', monospace; font-size: 12px; }
.st-uid-val { color: #c084fc; letter-spacing: 0.04em; }
.st-uid-badge {
    display: inline-flex; align-items: center; gap: 5px;
    background: rgba(167,93,237,0.12); border: 1px solid rgba(167,93,237,0.25);
    border-radius: 6px; padding: 3px 8px; cursor: pointer; margin-top: 4px;
    transition: background 0.2s, border-color 0.2s; user-select: none;
}
.st-uid-badge:hover { background: rgba(167,93,237,0.2); border-color: rgba(167,93,237,0.45); }
.st-uid-badge-label { font-size: 9px; font-weight: 700; letter-spacing: 0.08em; color: rgba(167,93,237,0.7); text-transform: uppercase; }
.st-uid-badge-val { font-size: 11px; font-weight: 700; color: #c084fc; font-family: 'Courier New', monospace; letter-spacing: 0.05em; }
.st-copy-btn {
    flex-shrink: 0;
    width: 28px; height: 28px; border-radius: 7px;
    background: rgba(167,93,237,0.08);
    border: 1px solid rgba(167,93,237,0.2);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #a75ded; transition: background 0.2s;
}
.st-copy-btn:hover { background: rgba(167,93,237,0.18); }

/* ── KYC Badge ── */
.st-kyc-badge {
    padding: 3px 8px; border-radius: 20px;
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
}
.st-kyc-badge--pending  { background: rgba(100,116,139,0.15); color: #94a3b8; }
.st-kyc-badge--reviewing{ background: rgba(245,158,11,0.15);  color: #fbbf24; }
.st-kyc-badge--approved { background: rgba(16,185,129,0.15);  color: #34d399; }
.st-kyc-badge--rejected { background: rgba(239,68,68,0.15);   color: #f87171; }

/* ── Upload dropzone ── */
.st-upload-block { margin-bottom: 12px; }
.st-upload-block:last-child { margin-bottom: 0; }
.st-drop-zone {
    width: 100%; box-sizing: border-box;
    padding: 20px 16px;
    background: rgba(10,4,22,0.6);
    border: 2px dashed rgba(167,93,237,0.25);
    border-radius: 10px;
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    cursor: pointer; color: rgba(255,255,255,0.4);
    transition: background 0.2s, border-color 0.2s, color 0.2s;
}
.st-drop-zone:hover {
    background: rgba(167,93,237,0.07);
    border-color: rgba(167,93,237,0.45);
    color: rgba(255,255,255,0.8);
}
.st-drop-label { font-size: 12px; font-weight: 500; text-align: center; }

/* ── KYC status states ── */
.st-kyc-state {
    display: flex; flex-direction: column; align-items: center;
    gap: 12px; padding: 24px 16px; text-align: center;
}
.st-kyc-state-icon {
    width: 60px; height: 60px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
}
.st-kyc-state-icon--amber { background: rgba(245,158,11,0.12); border: 2px solid rgba(245,158,11,0.3); color: #fbbf24; }
.st-kyc-state-icon--green { background: rgba(16,185,129,0.12); border: 2px solid rgba(16,185,129,0.3); color: #34d399; }
.st-kyc-state-text { font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.8); }
</style>
